import {
  Col,
  Row,
  Icon,
  Input,
  Table,
  Button,
  Drawer,
  Switch,
  Divider,
  Tooltip,
  Checkbox,
} from "antd";
import React, {
  useRef,
  useMemo,
  Fragment,
  useState,
  useEffect,
  useCallback,
} from "react";
import * as R from "ramda";
import moment from "moment";
import classnames from "classnames";
import { interval, merge, startWith, Subject } from "rxjs";

import {
  getKey,
  uploadFile,
  getMd5OfFile,
  sizeToHumanReadableSize,
  isLessThanHumanReadableSize,
} from "./upload-resource-util";
import {
  getOneFile,
  UploadStatusMap,
  insertOrUpdateFile,
  updateFileStatusById,
  getOrderListByMd5List,
} from "./indexed-db-manipulation";
import { Space } from "@/components/Space";
import { addTag } from "../aliyun-service-api";
import { registerResource } from "./useCustomRequest";
import { useWindowHeight } from "../hooks/use-window-height";
import { SmartSingleLine } from "@/components/SmartSingleLine";
import { validateENV } from "@/components/FileViewerV2/FileWatcher";
import { uploadResourceChannelMap } from "../tab-pages/video-resource-page";
import { TooltipIcon } from "@/containers/Video/widgets/dropdown-select-upload-type";
import { uploadResourceVideoLimit } from "@/containers/Video/widgets/upload-resource-modal";

export const getAllFileListRecursively = async (
  fileSystemDirectoryHandle,
  result = [],
  filePath = []
) => {
  if (
    !fileSystemDirectoryHandle ||
    typeof fileSystemDirectoryHandle.entries !== "function"
  ) {
    throw new Error("Invalid fileSystemDirectoryHandle");
  }

  const entries = [];
  for await (const entry of fileSystemDirectoryHandle.entries()) {
    entries.push(entry);
  }

  for (const [name, entry] of entries) {
    const file = await entry.getFile();
    if (entry.kind === "file" && file.type.includes("video")) {
      result.push({
        entry,
        filepath: [...filePath, name].join("/"),
      });
    } else if (entry.kind === "directory") {
      await getAllFileListRecursively(entry, result, [...filePath, name]);
    }
  }

  return result;
};

export const getFileRecursivelyByMd5 = async (
  fileSystemDirectoryHandle,
  md5
) => {
  for await (const [, entry] of fileSystemDirectoryHandle.entries()) {
    const file = await entry.getFile();
    const currentMd5 = await getMd5OfFile(file);
    if (currentMd5 === md5) {
      return file;
    }
  }
  return null;
};

export const CloudIcon = ({
  spin = false,
  toggle = R.T,
  loaded,
  onClick = R.T,
}) => {
  return (
    <div
      onClick={async () => {
        if (!loaded) {
          onClick();
        } else {
          toggle();
        }
      }}
      className="flex position-relative cursor-pointer"
    >
      <Icon
        type="sync"
        style={{
          bottom: "50%",
          left: "50%",
          transform: "translateX(-50%) translateY(60%) scale(0.5)",
        }}
        theme="outlined"
        spin={spin}
        className="position-absolute"
      />
      <Icon
        type="cloud"
        theme={spin || loaded ? "twoTone" : "outlined"}
        style={{
          fontSize: 24,
        }}
      />
    </div>
  );
};

export const getDiskFiles = async (fileSystemDirectoryHandle) => {
  const _files = [];
  await getAllFileListRecursively(fileSystemDirectoryHandle, _files, [
    fileSystemDirectoryHandle.name,
  ]);
  return _files;
};

export const getDiskFileByDbFile = async (
  fileSystemDirectoryHandle,
  dbFile
) => {
  return getFileRecursivelyByMd5(fileSystemDirectoryHandle, dbFile.sha);
};

export const useShowDirectoryPicker = (
  orderId,
  setFileSystemDirectoryHandle = R.T
) => {
  return useCallback(async () => {
    if (await validateENV()) {
      // noinspection JSUnresolvedReference
      setFileSystemDirectoryHandle(
        await window.showDirectoryPicker({
          id: `auto-upload-for-${orderId}`,
          mode: `readwrite`,
          startIn: `downloads`,
        })
      );
    } else {
    }
  }, [orderId]);
};

export const UploadTable = ({
  dataSource = [],
  retryOneFile = R.T,
  scrollHeight = 400,
}) => {
  return (
    <Table
      style={{ maxHeight: scrollHeight, overflow: "auto" }}
      dataSource={dataSource}
      pagination={false}
      columns={[
        {
          title: "文件名",
          dataIndex: "name",
          width: 100,
          render(name) {
            return (
              <div style={{ maxWidth: 100 }}>
                <SmartSingleLine>{name}</SmartSingleLine>
              </div>
            );
          },
        },
        {
          title: "大小",
          dataIndex: "size",
          width: 80,
          render: sizeToHumanReadableSize,
        },
        {
          title: "上传进度",
          dataIndex: "percentage",
          render(percentage, item) {
            switch (item.status) {
              case UploadStatusMap.未上传:
                return <span className="color-gray">0%</span>;
              case UploadStatusMap.上传中:
                // console.log("percentage", item);
                return (
                  <span className="color-green">
                    {(percentage || 0).toFixed(2)}%
                  </span>
                );
              case UploadStatusMap.上传成功:
                return <span className="color-green">100%</span>;
              case UploadStatusMap.上传失败:
                return <span className="color-danger">失败</span>;
              default:
                return null;
            }
          },
        },
        {
          title: "状态",
          dataIndex: "status",
          render: (status, item) => {
            switch (status) {
              case UploadStatusMap.未上传:
                return <span className="color-gray">未上传</span>;
              case UploadStatusMap.上传中:
                return <span className="color-green">上传中</span>;
              case UploadStatusMap.上传成功:
                return <span className="color-green">上传成功</span>;
              case UploadStatusMap.上传失败:
                const { size } = item;
                return (
                  <Space>
                    <span className="color-danger">
                      上传失败 {sizeToHumanReadableSize(size)}
                    </span>
                    <Tooltip title="重新上传">
                      <Icon
                        className="cursor-pointer"
                        type="redo"
                        onClick={() => {
                          retryOneFile(item.id);
                        }}
                      />
                    </Tooltip>
                  </Space>
                );
              default:
                return null;
            }
          },
        },
        {
          title: "创建时间",
          dataIndex: "id",
          render(percentage, item) {
            switch (item.status) {
              case UploadStatusMap.未上传:
                return "未上传";
              case UploadStatusMap.上传中:
                return "上传中";
              case UploadStatusMap.上传成功:
                return moment(item.upload_date).format("YYYY-MM-DD HH:mm:ss");
              case UploadStatusMap.上传失败:
                return moment(item.upload_date).format("YYYY-MM-DD HH:mm:ss");
              default:
                return null;
            }
          },
        },
      ]}
    />
  );
};

export const FileUploadForm = ({
  folderName,
  onClickInput = R.T,
  onClearInput = R.T,
  tagList = [],
  uploading = false,
  toggleUpload = R.T,
  onChange = R.T,
}) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [isPublic, setIsPublic] = useState(false);
  const [useAI, setUseAI] = useState(false);

  useEffect(() => {
    onChange({
      tag: selectedTags,
      hidden: isPublic ? 0 : 1,
      ai: useAI ? 1 : 0,
    });
  }, [selectedTags, isPublic, useAI]);

  return (
    <Space>
      <Row className="flex-y-center">
        <Col className="text-align-right pr-2" span={6}>
          待上传目录:
        </Col>
        <Col span={18}>
          <Space className="flex-y-center">
            <Input
              disabled={uploading}
              value={folderName}
              onClick={onClickInput}
            />
            <Icon
              type="delete"
              className={classnames({
                "color-link cursor-pointer": Boolean(folderName),
                "opacity-50 cursor-forbidden":
                  (Boolean(folderName) && uploading) || R.isEmpty(folderName),
              })}
              onClick={() => {
                if (uploading) {
                  return;
                }
                if (folderName) {
                  onClearInput();
                }
              }}
            />
          </Space>
        </Col>
      </Row>
      <Row>
        <Col className="text-align-right pr-2" span={6}>
          上传到标签:
        </Col>
        <Col
          span={18}
          style={{
            position: "relative",
            left: -8,
          }}
        >
          <Checkbox.Group
            disabled={uploading}
            onChange={(checkedValues) => setSelectedTags(checkedValues)}
          >
            {tagList.map((tag) => (
              <Checkbox
                style={{
                  marginLeft: 8,
                }}
                key={tag.id}
                value={tag.id}
              >
                {tag.name}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Col>
      </Row>
      <Row className="flex-y-center">
        <Col className="text-align-right pr-2" span={6}>
          所上传视频:
        </Col>
        <Col span={18}>
          <Switch
            disabled={uploading}
            checkedChildren="公开"
            unCheckedChildren="隐藏"
            onChange={(checked) => setIsPublic(checked)}
          />
        </Col>
      </Row>
      <div className="hidden">
        <Row className="flex-y-center">
          <Col className="text-align-right pr-2" span={6}>
            AI智能剪辑:
          </Col>
          <Col span={18}>
            <Switch
              disabled={uploading}
              onChange={(checked) => setUseAI(checked)}
            />
          </Col>
        </Row>
      </div>
      <Row className="flex-y-center">
        <Col span={18} push={6} className="flex-y-center">
          <Button
            disabled={!folderName}
            ghost={uploading}
            onClick={toggleUpload}
            style={{ width: 200, marginTop: 10 }}
            type={uploading ? "danger" : "primary"}
          >
            {uploading ? "停止上传" : "开始上传"}
          </Button>
          <span className="position-relative hidden" style={{ top: 6 }}>
            <TooltipIcon title="正在上传的文件不能被终止" />
          </span>
        </Col>
      </Row>
    </Space>
  );
};

export const UploadCloudUI = ({
  openDrawer = R.T,
  children,
  spinning = false,
}) => {
  return (
    <div
      className="flex-y-center position-relative"
      style={{
        padding: "4px 16px",
        background: "#f0f2f5",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      <CloudIcon spin={spinning} onClick={openDrawer} />
      <span
        className="color-gray ml-2 position-relative"
        style={{ top: 2, fontSize: 12, opacity: 0.7 }}
      >
        {children}
      </span>
    </div>
  );
};

export const HeaderInfo = ({ files = [] }) => {
  return (
    <Space className="flex">
      <div>
        上传中:{" "}
        {files.filter(R.propEq(UploadStatusMap.上传中, "status")).length}
      </div>
      <div>
        待上传:{" "}
        {files.filter(R.propEq(UploadStatusMap.未上传, "status")).length}
      </div>
      <div>
        上传成功:{" "}
        {files.filter(R.propEq(UploadStatusMap.上传成功, "status")).length}
      </div>
      <div className="color-danger">
        失败未处理:{" "}
        {files.filter(R.propEq(UploadStatusMap.上传失败, "status")).length}
      </div>
    </Space>
  );
};

export const useAfterDone = (orderId, ref) => {
  return useCallback(
    (res) => {
      const tagList = R.pathOr([], ["current", "tag"], ref);
      if (tagList.length > 0) {
        const id = R.pathOr("", ["id"], res);
        addTag(orderId, id, tagList);
      }
    },
    [orderId]
  );
};

const useScrollHeight = (ref, fileSystemDirectoryHandle = {}) => {
  const windowHeight = useWindowHeight();
  return useMemo(() => {
    if (!ref.current) {
      return 400;
    }
    const refHeight = ref.current.getBoundingClientRect().top;
    return Math.max(400, windowHeight - refHeight - 70);
  }, [ref, windowHeight, JSON.stringify(fileSystemDirectoryHandle)]);
};

const CurrentHeightComponent = ({ children = R.T }) => {
  const ref = useRef();

  const scrollHeight = useScrollHeight(ref);
  return <div ref={ref}>{children({ scrollHeight })}</div>;
};

export const CloudChildren = ({ hasChosenFolder, files }) => {
  if (!hasChosenFolder) {
    return null;
  }
  return (
    <Fragment>
      {files.filter(R.propEq(UploadStatusMap.上传成功, "status")).length} /{" "}
      {files.length}
    </Fragment>
  );
};

export const AutoUploadVideoHelperRxjs = (props) => {
  //  是否显示上传视频的抽屉
  const [visible, setVisible] = useState(false);

  //  额外的参数, 用于上传时候请求的参数
  const extraParamsRef = useRef({});

  const abortionRef = useRef(null);

  /************ uploading start **************/
  const [uploading, setUploading] = useState(false);
  const uploadingButtonStatusRef = useRef(uploading);
  const currentIsUploadingFileRef = useRef(false);
  useEffect(() => {
    //  手动终止上传, 正在上传的显示为上传失败
    uploadingButtonStatusRef.current = uploading;
    if (!uploading) {
      if (abortionRef.current) {
        abortionRef.current();
        setFiles((_files) => {
          return _files.map((file) => {
            if (file.status === UploadStatusMap.上传中) {
              return {
                ...file,
                status: UploadStatusMap.上传失败,
              };
            }
            return file;
          });
        });
      }
    }
  }, [uploading]);
  const toggleUpload = useCallback(() => {
    setUploading(!uploading);
  }, [uploading]);
  /**************** uploading end **************/

  /**************** showDirectoryPicker start **************/
  const [addFileSystemDirectorySubject$] = useState(new Subject());
  const [fileSystemDirectoryHandle, setFileSystemDirectoryHandle] = useState(
    {}
  );
  const fileSystemDirectoryHandleRef = useRef(fileSystemDirectoryHandle);
  useEffect(() => {
    fileSystemDirectoryHandleRef.current = fileSystemDirectoryHandle;
    if (R.isEmpty(fileSystemDirectoryHandle)) {
      setFiles([]);
    } else {
      addFileSystemDirectorySubject$.next();
    }
  }, [fileSystemDirectoryHandle]);
  /**************** showDirectoryPicker end **************/

  const [files, setFiles] = useState([]);

  const showDirectoryPicker = useShowDirectoryPicker(
    props.orderId,
    setFileSystemDirectoryHandle
  );

  //  上传成功后的回调, 主要目的是将标签添加到视频上
  const afterEachDone = useAfterDone(props.orderId, extraParamsRef);

  useEffect(() => {
    if (!props.orderId) {
      return;
    }

    /**
     * 这儿基本的逻辑是
     * 1. 每隔 uploadCheckInterval 进行check当前是否在上传, 没有上传就取一个进行上传
     * 2. 每隔 checkFolderChangeInterval 检查文件夹是否发生变化, 如果发生变化, 则重新获取文件列表
     */
    const uploadCheckInterval = 2000;
    const checkFolderChangeInterval = 10000;

    let currentUploadingFile = {
      sha: null,
      percentage: 0,
    };

    const uploadIntervalSubscription = interval(uploadCheckInterval).subscribe(
      async () => {
        //  如果当前没有选择文件夹, 则不进行上传
        if (R.isEmpty(fileSystemDirectoryHandleRef.current)) {
          return;
        }

        //  如果 上传按钮 没有被点击, 则不进行上传
        if (!uploadingButtonStatusRef.current) {
          return;
        }
        //  如果当前有文件正在上传, 则不进行上传
        if (currentIsUploadingFileRef.current) {
          return;
        }

        try {
          currentIsUploadingFileRef.current = true;
          //  这个模块是真正开始上传的模块
          const userId = R.pathOr("0", ["user", "id"], props) * 1;
          const fileObjInDb = await getOneFile(props.orderId);
          if (!fileObjInDb) {
            currentIsUploadingFileRef.current = false;
            return;
          }
          const realFileObject = await getDiskFileByDbFile(
            fileSystemDirectoryHandleRef.current,
            fileObjInDb
          );
          if (!realFileObject) {
            currentIsUploadingFileRef.current = false;
            return;
          }

          //  检查大小, isLessThanHumanReadableSize(file.size, "200MB");
          if (
            !isLessThanHumanReadableSize(
              realFileObject.size,
              uploadResourceVideoLimit
            )
          ) {
            // noinspection ES6MissingAwait
            updateFileStatusById(fileObjInDb.id, UploadStatusMap.上传失败);
            currentIsUploadingFileRef.current = false;
            return;
          }

          // console.log(realFile);
          // const realFileObject = await realFile.entry.getFile();

          currentUploadingFile = {
            sha: fileObjInDb.sha,
            percentage: 0,
          };

          const key = getKey(props.orderId, "", userId, realFileObject);
          uploadFile(
            key,
            realFileObject,
            (percentage) => {
              if (!uploadingButtonStatusRef.current) {
                return;
              }
              const n = percentage * 100;
              const complete = n >= 100;
              setFiles((_files) => {
                return _files.map((file) => {
                  if (file.sha === currentUploadingFile.sha) {
                    return {
                      ...file,
                      status: complete
                        ? UploadStatusMap.上传成功
                        : UploadStatusMap.上传中,
                    };
                  }
                  return file;
                });
              });
              currentUploadingFile.percentage = percentage * 100;
            },
            abortionRef
          )
            .then(() => {
              //  更新innoDB中的文件状态
              updateFileStatusById(fileObjInDb.id, UploadStatusMap.上传成功);
              //  注册资源
              registerResource(realFileObject, key, props.orderId, userId, {
                channel: uploadResourceChannelMap.本地自动上传,
                // desc: `自动上传:${moment().format("YYYY-MM-DD HH:mm:ss")}`,
                desc: '',
                ...R.pathOr({}, ["current"], extraParamsRef),
                //  下面的then是打标签
              }).then(afterEachDone);
            })
            .catch(() => {
              updateFileStatusById(fileObjInDb.id, UploadStatusMap.上传失败);
            })
            .finally(() => {
              currentIsUploadingFileRef.current = false;
              currentUploadingFile.sha = null;
            });
        } catch (e) {
          console.error(e);
          currentIsUploadingFileRef.current = false;
        }
      }
    );

    let previousDiskFiles = [];
    const checkFolderSubscription = merge(
      addFileSystemDirectorySubject$,
      interval(checkFolderChangeInterval)
    )
      .pipe(startWith(0))
      .subscribe(async () => {
        if (R.isEmpty(fileSystemDirectoryHandleRef.current)) {
          return;
        }
        const diskFiles = await getDiskFiles(
          fileSystemDirectoryHandleRef.current
        );
        if (R.equals(previousDiskFiles, diskFiles)) {
          return;
        }
        previousDiskFiles = diskFiles;
        await insertOrUpdateFile(
          props.orderId,
          diskFiles.map(R.pathOr({}, ["entry"])),
          diskFiles.map(R.pathOr("", ["filepath"]))
        );
        const md5List = await Promise.all(
          diskFiles.map(async (file) => {
            return getMd5OfFile(await file.entry.getFile());
          })
        );
        const files = await getOrderListByMd5List(props.orderId, md5List);
        setFiles(
          R.flatten(files).map((file) => {
            if (file.sha === currentUploadingFile.sha) {
              return {
                ...file,
                percentage: currentUploadingFile.percentage,
                status: UploadStatusMap.上传中,
              };
            }
            return file;
          })
        );
      });

    return () => {
      uploadIntervalSubscription.unsubscribe();
      checkFolderSubscription.unsubscribe();
    };
    //  这个依赖可以保证, 这个副作用只执行一次
  }, [props.orderId]);

  return (
    <Fragment>
      <Drawer
        width={600}
        visible={visible}
        title="自动上传视频"
        onClose={() => setVisible(false)}
      >
        <FileUploadForm
          folderName={R.pathOr("", ["name"], fileSystemDirectoryHandle)}
          onClickInput={showDirectoryPicker}
          onClearInput={() => setFileSystemDirectoryHandle({})}
          tagList={R.defaultTo([], R.pathOr([], ["tagList"], props))}
          uploading={uploading}
          toggleUpload={toggleUpload}
          onChange={(values) => (extraParamsRef.current = values)}
        />
        <Divider />
        <Space>
          <HeaderInfo files={files} />
          <CurrentHeightComponent>
            {({ scrollHeight }) => (
              <UploadTable
                dataSource={files}
                retryOneFile={(fileId) =>
                  updateFileStatusById(fileId, UploadStatusMap.未上传)
                }
                scrollHeight={scrollHeight}
              />
            )}
          </CurrentHeightComponent>
        </Space>
      </Drawer>
      <UploadCloudUI openDrawer={() => setVisible(true)} spinning={uploading}>
        <CloudChildren
          files={files}
          hasChosenFolder={!R.isEmpty(fileSystemDirectoryHandle)}
        />
      </UploadCloudUI>
    </Fragment>
  );
};



// WEBPACK FOOTER //
// ./src/containers/Video/widgets/file-upload-helper.js